import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { AppError, parseHttpError } from './app-error.model';
import { ErrorModalComponent } from './error-modal/error-modal.component';

/**
 * Single entry point for error display, driven by the global HTTP interceptor (HTTP errors) and by
 * components for their own client-side errors. Picks the surface by severity rather than by which
 * component made the call (see the tier table in
 * .github/instructions/references/configurator-exceptions-frontend.md §3):
 *
 *  - toast        → success/info, and transient minor failures (e.g. clipboard). Auto-dismisses, so
 *                   never used for errors the user must act on.
 *  - field errors → 400 + `errors`; exposed via the `fieldErrors` signal for the signals-form to render.
 *  - modal        → recoverable 4xx (404/409, warning-styled) and blocking (403, tenant-db 404); shown here
 *                   via {@link ErrorModalComponent} and must be dismissed. Replaced the former inline banner,
 *                   which pushed the page content down.
 *  - redirect     → 401; session expired → navigate to login.
 *  - full-page    → 500 / unknown / transport; navigates to `/error`.
 */
@Injectable({ providedIn: 'root' })
export class ErrorService {
  private readonly toastr = inject(ToastrService);
  private readonly modal = inject(NgbModal);
  private readonly router = inject(Router);

  /** Most recent parsed error (any severity). */
  readonly lastError = signal<AppError | null>(null);
  /** Per-field validation messages for the current form; cleared on the next successful submit. */
  readonly fieldErrors = signal<Record<string, string[]> | null>(null);

  private modalOpen = false;

  // --- Success / info / transient toasts (the only correct use of a toast) ---
  success(message: string, title?: string): void {
    this.toastr.success(message, title);
  }

  info(message: string, title?: string): void {
    this.toastr.info(message, title);
  }

  /**
   * Transient, minor failure that does not block the user (e.g. "couldn't copy to clipboard"). Shown as
   * an auto-dismissing toast — correct because there is nothing to act on. Do NOT use for failures the
   * user must see or recover from (use {@link notify} or let the interceptor handle the HTTP error).
   */
  warning(message: string, title?: string): void {
    this.toastr.warning(message, title);
  }

  /**
   * Surface a client-side (non-HTTP) error the user must see — a guard/validation message or a permission
   * check that didn't come from the server. Always shown in the dismissable modal; `tier: 'modal'` only
   * makes it blocking-styled (red) instead of warning-styled. HTTP errors should NOT come through here —
   * they are handled by {@link handle} via the global interceptor.
   */
  notify(message: string, opts?: { title?: string; tier?: 'warning' | 'modal' }): void {
    const appError: AppError = {
      status: 0,
      severity: opts?.tier === 'modal' ? 'blocking' : 'recoverable',
      title: opts?.title ?? 'Der opstod en fejl',
      message,
      correlationId: null,
      isTransport: false,
      raw: null,
    };
    this.lastError.set(appError);
    this.showModal(appError);
  }

  /**
   * Routes an HTTP (or runtime) error to its tier and returns the parsed {@link AppError}. Called by the
   * global interceptor. Does NOT swallow — the interceptor rethrows so existing component `.catch()`es
   * still run.
   */
  handle(err: unknown): AppError {
    const appError = parseHttpError(err);
    this.lastError.set(appError);

    switch (appError.severity) {
      case 'validation':
        this.fieldErrors.set(appError.fieldErrors ?? {});
        break;
      case 'recoverable':
      case 'blocking':
        this.showModal(appError);
        break;
      case 'redirect':
        this.goToLogin();
        break;
      case 'fatal':
        this.goToErrorPage();
        break;
    }

    return appError;
  }

  /** Opens the blocking error modal. Guarded so a burst of failures doesn't stack modals. */
  showModal(appError: AppError): void {
    if (this.modalOpen) {
      return;
    }
    this.modalOpen = true;
    const ref = this.modal.open(ErrorModalComponent, {
      backdrop: 'static',
      keyboard: true,
      scrollable: true,
      size: 'xl',
    });
    (ref.componentInstance as ErrorModalComponent).error = appError;
    ref.result.finally(() => (this.modalOpen = false));
  }

  goToErrorPage(): void {
    void this.router.navigate(['/error']);
  }

  /** 401 — the session is no longer valid; send the user to log in again. */
  goToLogin(): void {
    void this.router.navigate(['/login']);
  }

  clearFieldErrors(): void {
    this.fieldErrors.set(null);
  }
}
